import React, { useMemo } from 'react';
import { Alert, Button, EmptyState, EmptyStateActions, EmptyStateBody, EmptyStateFooter } from '@patternfly/react-core';
import { GlobeIcon, PlusCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatPrice } from '../../../utils/money';
import type { DomainDefaultCell, DomainOperation, DomainTld } from '../../../types/models/DomainPricing';
import type {
  ApiResponseDomainCellCleared,
  ApiResponseDomainDefaultCell,
} from '../../../types/api/ApiResponseDomainPricing';
import DomainMatrixTable from '@app/DomainMatrixTable';
import DomainPriceCell, { type CellMutationResult } from './DomainPriceCell';

interface DomainDefaultMatrixTabProps {
  operations: DomainOperation[];
  /** Priceable (active) TLDs — the matrix rows. */
  tlds: DomainTld[];
  cells: DomainDefaultCell[];
  /** Whether the matrix is (re)loading — drives the Refresh button spinner. */
  loading: boolean;
  /** A warm-refresh error (the matrix is already loaded) — surfaced inline above the table. */
  error: string | null;
  refetch: () => Promise<void>;
  applyCell: (slug: string, tld: string, unitPrice: string | null) => void;
  /** Opens the Add TLD modal (for the empty-state CTA). */
  onAddTld: () => void;
}

// The platform-default side of the matrix: every cell is staff-editable directly. PUT/DELETE
// /admin/products/domains/{slug}/{tld} write the clientID IS NULL default; both branch on the JSend
// body.status, notify + open the drawer on both outcomes, and update the cell in place.
function DomainDefaultMatrixTab({
  operations,
  tlds,
  cells,
  loading,
  error,
  refetch,
  applyCell,
  onAddTld,
}: DomainDefaultMatrixTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const defaultMap = useMemo(() => {
    const map = new Map<string, string>();
    cells.forEach((c) => map.set(`${c.slug}:${c.tld}`, c.unit_price));
    return map;
  }, [cells]);

  const saveCell = async (op: DomainOperation, tld: DomainTld, unitPrice: string): Promise<CellMutationResult> => {
    try {
      const response = await apiFetch(`/admin/products/domains/${op.slug}/${tld.tld}`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: unitPrice }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseDomainDefaultCell | null;
      if (body?.status === 'success' && body.data?.cell) {
        applyCell(op.slug, tld.tld, body.data.cell.unit_price);
        addNotification({
          title: 'Default price updated',
          srTitle: 'Success alert',
          description: `${op.label} for ${tld.label} is now ${formatPrice(body.data.cell.unit_price)} (excl. VAT).`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return { ok: true, fieldError: null };
      }
      const fieldError = body?.data?.unit_price ?? null;
      addNotification({
        title: 'Could not update price',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not update the price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not update price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const clearCell = async (op: DomainOperation, tld: DomainTld): Promise<{ ok: boolean }> => {
    try {
      const response = await apiFetch(`/admin/products/domains/${op.slug}/${tld.tld}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponseDomainCellCleared | null;
      if (body?.status === 'success' && body.data?.cleared) {
        applyCell(op.slug, tld.tld, null);
        addNotification({
          title: 'Default price cleared',
          srTitle: 'Success alert',
          description: `${op.label} for ${tld.label} is now unpriced.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return { ok: true };
      }
      addNotification({
        title: 'Could not clear price',
        srTitle: 'Error alert',
        description: body?.message ?? 'Could not clear the price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    } catch (err) {
      addNotification({
        title: 'Could not clear price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    }
  };

  const renderCell = (op: DomainOperation, tld: DomainTld) => {
    const value = defaultMap.get(`${op.slug}:${tld.tld}`) ?? null;
    return (
      <DomainPriceCell
        cellKey={`def-${op.slug}-${tld.tld}`}
        unitPrice={value}
        editable
        ariaContext={`${op.label} for ${tld.label}`}
        description={op.product_desc}
        onSave={(v) => saveCell(op, tld, v)}
        onClear={() => clearCell(op, tld)}
        canClear={value !== null}
        clearLabel="Clear"
      />
    );
  };

  const refreshButton = (
    <Button
      variant="secondary"
      icon={<SyncAltIcon />}
      onClick={() => void refetch()}
      isLoading={loading}
      isDisabled={loading}
    >
      Refresh
    </Button>
  );

  const emptyState = (
    <EmptyState titleText="No TLDs yet" icon={GlobeIcon} variant="lg" headingLevel="h2">
      <EmptyStateBody>Add a TLD to start setting default domain prices.</EmptyStateBody>
      <EmptyStateFooter>
        <EmptyStateActions>
          <Button variant="primary" icon={<PlusCircleIcon />} onClick={onAddTld}>
            Add TLD
          </Button>
        </EmptyStateActions>
      </EmptyStateFooter>
    </EmptyState>
  );

  return (
    <>
      {/* A failed manual Refresh keeps the (stale) matrix visible — surface the error inline rather
          than blanking the table (the cold-load failure is handled at the page level). */}
      {error && (
        <Alert variant="danger" isInline title="Could not refresh domain pricing" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      <DomainMatrixTable
        operations={operations}
        tlds={tlds}
        ariaLabel="Default domain prices matrix"
        renderCell={renderCell}
        toolbarActions={refreshButton}
        emptyState={emptyState}
      />
    </>
  );
}

export default DomainDefaultMatrixTab;
